import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Plus, FileText, Clock, CheckCircle, MessageSquare, X, Check, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { SearchableSelect } from './ui/searchable-select';
import { complaintsApi, directoryApi, uploadsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const Complaints = () => {
  const backendBase = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState({ filed_by_me: 0, against_me: 0, pending: 0, resolved: 0 });
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewComplaintDialog, setShowNewComplaintDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  const { member } = useAuth();
  const currentMemberId = member?.member_id;

  const [complaintForm, setComplaintForm] = useState({
    against_company_id: '',
    against_company_name: '',
    against_company_country: '',
    against_company_office: '',
    against_person_name: '',
    communication_member_ids: [],
    category: '',
    priority: 'medium',
    description: '',
    attachments: []
  });

  const [resolveForm, setResolveForm] = useState({
    resolution_notes: '',
    resolved_by: 'member'
  });

  const categories = [
    'Payment Delay',
    'Service Quality',
    'Communication Issues',
    'Pricing Dispute',
    'Contract Violation',
    'Unprofessional Behavior',
    'Other'
  ];

  const fetchData = useCallback(async () => {
    if (!currentMemberId) return;
    try {
      setLoading(true);
      const [complaintsRes, statsRes, companiesRes, countriesRes] = await Promise.all([
        complaintsApi.getComplaints(currentMemberId, { as_filed_by: true }),
        complaintsApi.getStats(currentMemberId),
        directoryApi
          .getCompaniesFromMembers({ exclude_member_id: currentMemberId, limit: 500 })
          .catch(() => directoryApi.getCompanies({ limit: 200 })),
        directoryApi.getCountries().catch(() => ({ data: [] }))
      ]);

      setComplaints(complaintsRes.data);
      setStats(statsRes.data);
      setCompanies(Array.isArray(companiesRes?.data) ? companiesRes.data : []);
      setCountries(Array.isArray(countriesRes?.data) ? countriesRes.data : []);
    } catch (error) {
      console.error('Error fetching complaints:', error);
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [currentMemberId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetComplaintForm = () => {
    setComplaintForm({
      against_company_id: '',
      against_company_name: '',
      against_company_country: '',
      against_company_office: '',
      against_person_name: '',
      communication_member_ids: [],
      category: '',
      priority: 'medium',
      description: '',
      attachments: []
    });
  };

  const availableCountries = Array.isArray(countries) && countries.length > 0
    ? countries
    : Array.from(new Set((companies || []).map((c) => (c?.country || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const availableCountryOptions = availableCountries.map((country) => ({ value: country, label: country }));

  const filteredCompanies = (companies || [])
    .filter((c) => c?.company_id && c?.company_name)
    .filter((c) => !complaintForm.against_company_country || c?.country === complaintForm.against_company_country);

  const handleAttachmentChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    try {
      setUploadingAttachments(true);
      const res = await uploadsApi.uploadMultiple(files, 'complaints', null, currentMemberId);
      const uploaded = Array.isArray(res.data?.files) ? res.data.files : [];
      const urls = uploaded.map((f) => f?.url).filter(Boolean);
      if (urls.length === 0) {
        throw new Error('No files uploaded');
      }
      setComplaintForm((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...urls]
      }));
      toast.success(`Uploaded ${urls.length} attachment${urls.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Attachment upload failed:', error);
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to upload attachment(s)'));
    } finally {
      setUploadingAttachments(false);
      // allow uploading same file again
      e.target.value = '';
    }
  };

  const removeAttachment = (url) => {
    setComplaintForm((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((a) => a !== url)
    }));
  };

  const handleSubmitComplaint = async (e) => {
    e.preventDefault();
    try {
      if (!complaintForm.against_company_country) {
        toast.error('Please select country');
        return;
      }
      if (!complaintForm.against_company_name) {
        toast.error('Please select company');
        return;
      }
      if (!complaintForm.category) {
        toast.error('Please select complaint category');
        return;
      }
      if (!complaintForm.priority) {
        toast.error('Please select priority');
        return;
      }
      if (!complaintForm.description?.trim()) {
        toast.error('Please enter details description');
        return;
      }
      await complaintsApi.fileComplaint(complaintForm, currentMemberId);
      toast.success('Complaint submitted successfully. Reference number will be sent to your email.');
      setShowNewComplaintDialog(false);
      resetComplaintForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to submit complaint'));
    }
  };

  const handleResolveComplaint = async () => {
    if (!selectedComplaint) return;

    try {
      await complaintsApi.resolveComplaint(selectedComplaint.complaint_id, resolveForm, currentMemberId);
      toast.success('Complaint marked as resolved');
      setShowResolveDialog(false);
      setSelectedComplaint(null);
      setResolveForm({ resolution_notes: '', resolved_by: 'member' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to resolve complaint'));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'resolved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" />Resolved</Badge>;
      case 'under_review':
        return <Badge className="bg-blue-100 text-blue-700"><Clock size={12} className="mr-1" />Under Review</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock size={12} className="mr-1" />Pending</Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-700"><Check size={12} className="mr-1" />Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge className="bg-red-100 text-red-700">High Priority</Badge>;
      case 'medium':
        return <Badge className="bg-amber-100 text-amber-700">Medium Priority</Badge>;
      case 'low':
        return <Badge className="bg-gray-100 text-gray-700">Low Priority</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const renderComplaintCard = (complaint, isAgainstMe = false) => (
    <div key={complaint.complaint_id} className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="font-semibold text-gray-900">
              {isAgainstMe
                ? `From: ${complaint.filed_by_member?.company_name || complaint.filed_by_id}`
                : `Against: ${
                    complaint.against_company_name ||
                    complaint.against_member?.company_name ||
                    complaint.against_member_id ||
                    'N/A'
                  }${complaint.against_company_country ? ` (${complaint.against_company_country})` : ''}`
              }
            </h3>
            {getStatusBadge(complaint.status)}
            {getPriorityBadge(complaint.priority)}
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
            <span>Category: {complaint.category}</span>
            <span>•</span>
            <span>Ref: {complaint.reference_number}</span>
          </div>
          <p className="text-sm text-gray-700 mb-2">{complaint.description}</p>
          {complaint.resolution && (
            <div className="mt-3 p-3 bg-green-50 rounded text-sm">
              <p className="font-medium text-green-900 mb-1">Resolution:</p>
              <p className="text-green-800">{complaint.resolution}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {complaint.status !== 'resolved' && complaint.status !== 'closed' && (
          <div className="flex flex-col space-y-2">
            <Button
              size="sm"
              variant="outline"
              className="text-green-600 border-green-600"
              onClick={() => {
                setSelectedComplaint(complaint);
                setShowResolveDialog(true);
              }}
              data-testid={`resolve-complaint-${complaint.complaint_id}`}
            >
              <Check size={14} className="mr-1" />
              Mark Resolved
            </Button>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Submitted: {new Date(complaint.created_at).toLocaleDateString()}</span>
        {complaint.resolved_at && (
          <span>Resolved: {new Date(complaint.resolved_at).toLocaleDateString()}</span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading complaints...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Complaint Management</h1>
          <p className="text-gray-600 mt-1">Report and track issues with members</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/complaints/grievance-redressal">
            <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="contact-oga-btn">
              <Mail size={18} className="mr-2" />
              Contact OGA
            </Button>
          </Link>
        <Dialog
          open={showNewComplaintDialog}
          onOpenChange={(open) => {
            setShowNewComplaintDialog(open);
            if (!open) resetComplaintForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="file-complaint-btn">
              <Plus size={18} className="mr-2" />
              File Complaint
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-10 pb-2 shrink-0">
              <DialogTitle>File a Complaint</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 pb-6">
            <form onSubmit={handleSubmitComplaint} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Please ensure you have attempted to resolve the issue directly with the member before filing a complaint. All complaints are reviewed by our compliance team.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Country of Company</Label>
                  <SearchableSelect
                    options={availableCountryOptions}
                    value={complaintForm.against_company_country}
                    onChange={(country) => {
                      setComplaintForm((prev) => ({
                        ...prev,
                        against_company_country: country,
                        against_company_id: '',
                        against_company_name: '',
                        against_company_office: '',
                        against_person_name: '',
                        communication_member_ids: []
                      }));
                    }}
                    placeholder="Search or select country"
                    allowOther={false}
                  />
                </div>

                <div>
                  <Label htmlFor="company">Company Name</Label>
                  <select
                    id="company"
                    className="w-full px-3 py-2 border rounded-md"
                    value={complaintForm.against_company_id}
                    onChange={(e) => {
                      const companyId = e.target.value;
                      const company = companies.find((c) => c.company_id === companyId);
                      setComplaintForm((prev) => ({
                        ...prev,
                        against_company_id: companyId,
                        against_company_name: company?.company_name || '',
                        against_company_country: company?.country || prev.against_company_country
                      }));
                    }}
                    disabled={!complaintForm.against_company_country}
                    required
                  >
                    <option value="">Select company...</option>
                    {filteredCompanies.map((c) => (
                      <option key={c.company_id} value={c.company_id}>
                        {c.company_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="companyOffice">Office (optional)</Label>
                  <Input
                    id="companyOffice"
                    value={complaintForm.against_company_office}
                    onChange={(e) => {
                      setComplaintForm((prev) => ({ ...prev, against_company_office: e.target.value }));
                    }}
                    placeholder="e.g. Mumbai branch"
                  />
                </div>

                <div>
                  <Label htmlFor="personName">Person / Team (optional)</Label>
                  <Input
                    id="personName"
                    value={complaintForm.against_person_name}
                    onChange={(e) => setComplaintForm((prev) => ({ ...prev, against_person_name: e.target.value }))}
                    placeholder="e.g. Ops team / Finance / Ground team / Name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Complaint Category</Label>
                  <select
                    id="category"
                    className="w-full px-3 py-2 border rounded-md"
                    value={complaintForm.category}
                    onChange={(e) => setComplaintForm({ ...complaintForm, category: e.target.value })}
                    required
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="priority">Priority Level</Label>
                  <select
                    id="priority"
                    className="w-full px-3 py-2 border rounded-md"
                    value={complaintForm.priority}
                    onChange={(e) => setComplaintForm({ ...complaintForm, priority: e.target.value })}
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Detailed Description</Label>
                <Textarea
                  id="description"
                  value={complaintForm.description}
                  onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
                  placeholder="Please provide a detailed description of the issue, including dates, transaction references, and any relevant details..."
                  rows={6}
                  required
                />
              </div>

              <div>
                <Label htmlFor="attachments">Attachments (optional)</Label>
                <Input
                  id="attachments"
                  type="file"
                  multiple
                  onChange={handleAttachmentChange}
                  disabled={uploadingAttachments}
                />
                {(complaintForm.attachments || []).length > 0 && (
                  <div className="mt-2 space-y-2">
                    {(complaintForm.attachments || []).map((url) => (
                      (() => {
                        const href = url?.startsWith('http') ? url : `${backendBase}${url}`;
                        return (
                      <div key={url} className="flex items-center justify-between text-sm border rounded-md px-3 py-2">
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline truncate pr-3"
                          title={url}
                        >
                          {url.split('/').pop()}
                        </a>
                        <Button type="button" variant="outline" size="sm" onClick={() => removeAttachment(url)}>
                          Remove
                        </Button>
                      </div>
                        );
                      })()
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowNewComplaintDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white">
                  Submit Complaint
                </Button>
              </div>
            </form>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">Total Complaint</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">{stats.filed_by_me}</p>
              </div>
              <FileText size={40} className="text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-900">Pending</p>
                <p className="text-3xl font-bold text-amber-700 mt-1">{stats.pending}</p>
              </div>
              <Clock size={40} className="text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900">Resolved</p>
                <p className="text-3xl font-bold text-green-700 mt-1">{stats.resolved}</p>
              </div>
              <CheckCircle size={40} className="text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Your Complaints */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Total Complaint ({complaints.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {complaints.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No complaints filed yet</p>
          ) : (
            <div className="space-y-4">
              {complaints.map((complaint) => renderComplaintCard(complaint, false))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Complaint</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Mark this complaint as resolved. Both parties can mark complaints as resolved when the issue has been addressed.
            </p>

            <div>
              <Label>Resolution Notes</Label>
              <Textarea
                value={resolveForm.resolution_notes}
                onChange={(e) => setResolveForm({ ...resolveForm, resolution_notes: e.target.value })}
                placeholder="Describe how the issue was resolved..."
                rows={4}
                required
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleResolveComplaint}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={!resolveForm.resolution_notes.trim()}
              >
                <Check size={14} className="mr-1" />
                Mark as Resolved
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info Card */}
      <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle size={24} className="text-blue-600 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Complaint Process Guidelines</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-2"></span>
                  Complaints are not only against members. Use <strong>Contact OGA</strong> above for general grievances or to reach One Globe Alliance directly (mail and grievance redressal).
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-2"></span>
                  All complaints are reviewed within 2-3 business days
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-2"></span>
                  Our compliance team investigates both parties fairly and impartially
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-2"></span>
                  Either party can mark a complaint as resolved when the issue is addressed
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-2"></span>
                  Serious violations may result in membership suspension or termination
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Complaints;
